import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { Button, ActionIcon, TextInput, Switch, Text } from '@mantine/core';
import { IconPlus, IconTrash, IconEdit } from '@tabler/icons-react';
import './AdminProducts.css';
import './ShippingListSection.css';

export default function ShippingListSection({ listKey, title, description }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setItems(await apiService.getAdminShippingList(listKey));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [listKey]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setError('');
    setAdding(true);
    try {
      await apiService.addShippingItem(listKey, newName.trim());
      setNewName('');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    try {
      await apiService.updateShippingItem(listKey, id, { is_active: !currentActive });
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleFreeShipping = async (id, currentFree) => {
    try {
      await apiService.updateShippingItem(listKey, id, { free_shipping: !currentFree });
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  const startEdit = (item) => { setEditingId(item.id); setEditName(item.name); setError(''); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    setError('');
    try {
      await apiService.updateShippingItem(listKey, id, { name: editName.trim() });
      setEditingId(null);
      setEditName('');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    try {
      await apiService.deleteShippingItem(listKey, id);
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  const activeCount = items.filter(i => i.is_active).length;

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2 className="settings-section-title">{title}</h2>
        <p className="settings-section-description">{description} {activeCount} active.</p>
      </div>

      <form onSubmit={handleAdd} className="settings-add-row">
        <TextInput
          placeholder="Add new..."
          value={newName}
          onChange={(e) => setNewName(e.currentTarget.value)}
          aria-label={`New ${listKey} name`}
          disabled={adding}
          flex={1}
        />
        <Button type="submit" color="green" leftSection={<IconPlus size={16} />} loading={adding} disabled={!newName.trim()}>
          Add
        </Button>
      </form>
      {error && (
        <Text id="shipping-list-error" role="alert" c="red" size="sm" mt="xs">
          {error}
        </Text>
      )}

      {loading ? (
        <p className="settings-section-loading" role="status" aria-live="polite">Loading...</p>
      ) : items.length === 0 ? (
        <Text role="status" aria-live="polite" c="dimmed" size="sm" mt="md">None configured. Add one above.</Text>
      ) : (
        <div className="table-responsive" style={{ marginTop: '1rem' }}>
          <table className="admin-table">
            <caption className="sr-only">{title}</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col" style={{ width: 80 }}>Active</th>
                {listKey === 'city' && <th scope="col" style={{ width: 90 }}>Free ship</th>}
                <th scope="col" style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className={!item.is_active ? 'row-inactive' : ''}>
                  <td>
                    {editingId === item.id ? (
                      <div className="inline-edit-row">
                        <TextInput
                          value={editName}
                          onChange={(e) => setEditName(e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdate(item.id);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          aria-label="Edit name"
                          size="sm"
                          autoFocus
                        />
                      </div>
                    ) : (
                      <strong>{item.name}</strong>
                    )}
                  </td>
                  <td>
                    <Switch size="sm" checked={item.is_active} onChange={() => handleToggleActive(item.id, item.is_active)} aria-label={`Toggle ${item.name}`} />
                  </td>
                  {listKey === 'city' && (
                    <td>
                      <Switch
                        size="sm"
                        checked={!!item.free_shipping}
                        onChange={() => handleToggleFreeShipping(item.id, item.free_shipping)}
                        disabled={!item.is_active}
                        aria-label={`Free shipping for ${item.name}`}
                      />
                    </td>
                  )}
                  <td>
                    <div className="inline-actions">
                      {editingId === item.id ? (
                        <>
                          <Button size="xs" variant="filled" onClick={() => handleUpdate(item.id)}>Save</Button>
                          <Button size="xs" variant="subtle" color="gray" onClick={cancelEdit}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <ActionIcon variant="subtle" color="gray" onClick={() => startEdit(item)} title="Edit"><IconEdit size={16} /></ActionIcon>
                          <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(item.id, item.name)} title="Delete"><IconTrash size={16} /></ActionIcon>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
